import { useState } from 'react';
import { 
  markAlertAsBroken,
  markAlertAsResolved,
  markAlertAsNotBroken
} from '../pages/ExperimentManagement/services/alertManagementApi';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const getSuccessMessage = (action) => {
  switch (action) {
    case 'set_resolution_date':
      return 'Resolution date set successfully';
    case 'confirm_broken':
      return 'Alert marked as broken';
    case 'confirm_not_broken':
      return 'Alert marked as not broken';
    default:
      return 'Alert updated successfully';
  }
};

export const useAlertActions = ({ onSuccess, showToast, preserveState }) => {
  const { currentUser } = useAuth();
  const [actionLoading, setActionLoading] = useState(null);
  
  
  const executeAction = async (alertIds, action, metadata = {}) => {
    const clientId = currentUser?.customer_id;
    
    
    if (!clientId) {
      console.error('Customer ID not available for alert action');
      showToast?.('Customer ID not available', 'error');
      return false;
    }
    
    // Ensure alertIds is always an array
    const ids = Array.isArray(alertIds) ? alertIds : [alertIds];
    
    // Preserve state before action (for restoration after refetch)
    if (preserveState && typeof preserveState === 'function') {
      preserveState();
    }
    
    setActionLoading(action);
    try {
      let result;
      
      switch (action) {
        case 'confirm_not_broken':
        case 'not_broken':
          result = await markAlertAsNotBroken(ids, clientId);
          break;
        case 'confirm_broken':
        case 'mark_as_broken':
          result = await markAlertAsBroken(ids, metadata?.planned_resolution_date, clientId);
          break;
        case 'set_resolution_date':
        case 'resolved':
          result = await markAlertAsResolved(ids, metadata?.resolution_date, clientId);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      if (result.success) {
        if (onSuccess) {
          onSuccess();
        }
        showToast?.(result.message || getSuccessMessage(action), 'success');
        return true;
      } else {
        showToast?.(result.message || `${action} failed`, 'error');
        return false;
      }
    } catch (error) {
      console.error(`Failed to execute ${action}:`, error);
      // Extract error message from API response if available
      const errorMessage = error.response?.data?.message || error.message || `${action} failed`;
      showToast?.(`Error: ${errorMessage}`, 'error');
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const submitResolutionDate = async (alertId, date) => {
    if (!date) return false;
    const formattedDate = typeof date.format === 'function' ? date.format('YYYY-MM-DD') : date;
    return executeAction(alertId, 'set_resolution_date', {
      resolution_date: formattedDate
    });
  };

  const confirmBroken = async (alertId, plannedDate) => {
    const formattedDate = plannedDate 
      ? (typeof plannedDate.format === 'function' ? plannedDate.format('YYYY-MM-DD') : plannedDate)
      : dayjs().add(1, 'day').format('YYYY-MM-DD'); // Default to tomorrow
    return executeAction(alertId, 'confirm_broken', {
      planned_resolution_date: formattedDate
    });
  };

  const confirmNotBroken = async (alertId) => {
    return executeAction(alertId, 'confirm_not_broken');
  };

  return {
    actionLoading,
    submitResolutionDate,
    confirmBroken,
    confirmNotBroken,
    executeAction // for any other actions
  };
};

export default useAlertActions;